import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import type {
  TruckEligibilityRow,
  EligibilityGateState,
} from "@/lib/truck-eligibility";

/**
 * Latest eligibility event per tractor, read from
 * `public.truck_eligibility_current` (one row per tractor, already
 * de-duplicated in SQL — never join the raw event table on hot paths).
 *
 * Returns an empty map and `gateState: "off"` when the caller passes
 * `enabled: false`, without hitting the network.
 *
 * Consumers must branch on `gateState`: an empty map only means "no records
 * exist" when the state is `ready`. While `loading`/`error`, selection and
 * saving are blocked without claiming the data is missing.
 */
export function useTruckEligibilityMap(opts?: { enabled?: boolean }) {
  const enabled = opts?.enabled ?? true;
  // Eligibility rows are tenant-scoped by RLS, so the cache must be too:
  // without the tenant in the key, switching tenants would serve the previous
  // tenant's map as `ready`. Keying on it forces a fresh `loading` state.
  const { activeTenant } = useTenant();

  const query = useQuery({
    queryKey: ["truck-eligibility-current", activeTenant],
    enabled,
    // Eligibility changes infrequently and the payload is small (one row per
    // truck); 60s keeps every surface consistent without extra chatter.
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("truck_eligibility_current" as never)
        .select(
          "tractor_id, status, allowed_modes, eligible_modes, reasons, constraint_tags, decided_by, revision, emitted_at, status_effective_at",
        );
      if (error) throw error;
      const rows = (data ?? []) as unknown as TruckEligibilityRow[];
      const byTractor = new Map<string, TruckEligibilityRow>();
      for (const r of rows) {
        if (r.tractor_id) byTractor.set(r.tractor_id, r);
      }
      return byTractor;
    },
  });

  const gateState: EligibilityGateState = !enabled
    ? "off"
    : query.isError
      ? "error"
      : query.data
        ? "ready"
        : "loading";

  return {
    eligibilityByTractor: query.data ?? new Map<string, TruckEligibilityRow>(),
    isLoading: query.isLoading,
    isError: query.isError,
    isReady: gateState === "ready",
    gateState,
    isEnabled: enabled,
  };
}

